import {Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {App} from '../../../shared/models/app';
import {AppsService} from '../../../core/services/apps.service';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import {FileUploadService} from '../../../core/services/file-upload.service';
import {InfoCircleComponent} from '../info-circle/info-circle.component';
import {NgClass} from '@angular/common';
import {SpinnerComponent} from '../spinner/spinner.component';
import {FormsModule} from '@angular/forms';
import {ObjectType} from 'src/shared/models/object.model';
import {DialogBoxButtonColor} from '../../../shared/models/dialog-box';
import {Permission} from '../../../shared/models/fga/permission';
import {PermissionsService} from '../../../core/services/permissions.service';
import {SessionService} from '../../../core/services/session.service';
import {Router} from '@angular/router';

@Component({
  selector: 'motn-app-global-settings',
  templateUrl: './app-global-settings.component.html',
  styleUrls: ['./app-global-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    InfoCircleComponent,
    NgClass,
    SpinnerComponent,
    FormsModule,
  ],
})
export class AppGlobalSettingsComponent implements OnInit, OnChanges {
  @Input() app: App;
  @Input() patchChildApps: boolean = false;

  // @Output() updatedName: EventEmitter<string> = new EventEmitter<string>();
  // @Output() updatedSubtitle: EventEmitter<string> = new EventEmitter<string>();
  // @Output() updatedPostalCode: EventEmitter<string> = new EventEmitter<string>();
  // @Output() updatedImage: EventEmitter<string> = new EventEmitter<string>();

  imgSource: string = '';
  postalCode: string;
  name: string;
  subtitle: string;

  @ViewChild('fileInput') fileInputEl: ElementRef;
  private selectedFile: File;

  editedPicture: boolean = false;
  editedPostalCode: boolean = false;
  editedName: boolean = false;
  editedSubtitle: boolean = false;

  submittingPicture: boolean = false;
  submittingPostalCode: boolean = false;
  submittingName: boolean = false;
  submittingSubtitle: boolean = false;

  canDelete: boolean = false;
  private currentAppId: string;


  constructor(
    private appsService: AppsService,
    private shadeTools: ShadeToolsService,
    private fileUploadService: FileUploadService,
    private permissionsService: PermissionsService,
    private session: SessionService,
    private router: Router,
  ) {
  }


  ngOnInit(): void {
  }


  ngOnChanges(changes: SimpleChanges) {
    this.name = this.app.Name;
    this.subtitle = this.app.Config?.Subtitle?.Text;
    this.postalCode = this.app.Config?.Location?.PostalCode;

    if (this.app.Image) {
      this.fileUploadService.fetch(this.app.Image).subscribe(
        (data) => {
          if (data) {
            this.imgSource = data.URL;
          }
        },
      );
    }

    // Check delete permission only when app ID changes (i.e., we're viewing a different app)
    if (this.app && this.app.ID !== this.currentAppId) {
      this.currentAppId = this.app.ID;

      this.permissionsService.queryPermissions({
        UserID: this.session.userId,
        ObjectID: this.app.ID,
        ObjectType: ObjectType.APP,
        Permissions: [Permission.CAN_DELETE],
      }).subscribe({
        next: (response) => {
          this.canDelete = response.Results.find(r => r.Permission === Permission.CAN_DELETE)?.Allowed || false;
        },
        error: () => {
          this.canDelete = false;
        }
      });
    }
  }


  selectFile(event) {
    this.editedPicture = true;
    this.selectedFile = event.target.files[0];
  }


  submitName() {
    if (!this.editedName || this.submittingName) {
      return;
    }

    this.submittingName = true;

    this.appsService.patchAppName$(this.app.ID, this.name).subscribe(
      () => {
        if (this.patchChildApps) {
          this.appsService.patchChildAppSubtitles$(this.app.ID, this.name).subscribe(
            () => {
              this.submittingName = false;
              this.editedName = false;
            },
            () => {
              this.submittingName = false;
              this.shadeTools.errorDialog();
            },
          );
        } else {
          this.submittingName = false;
          this.editedName = false;
        }
      },
      () => {
        this.submittingName = false;
        this.shadeTools.errorDialog();
      },
    );
  }


  submitSubtitle() {
    if (!this.editedSubtitle || this.submittingSubtitle) {
      return;
    }

    this.submittingSubtitle = true;

    this.appsService.patchAppSubtitle$(this.app.ID, this.subtitle).subscribe(
      () => {
        this.submittingSubtitle = false;
        this.editedSubtitle = false;
      },
      () => {
        this.shadeTools.errorDialog();
      },
    );
  }


  submitPostalCode() {
    if (!this.editedPostalCode || this.submittingPostalCode) {
      return;
    }

    this.submittingPostalCode = true;

    this.appsService.patchAppPostalCode$(this.app.ID, this.postalCode).subscribe(
      () => {
        if (this.patchChildApps) {
          this.appsService.patchChildAppPostalCodes$(this.app.ID, this.postalCode).subscribe(
            () => {
              this.submittingPostalCode = false;
              this.editedPostalCode = false;
            },
            () => {
              this.submittingPostalCode = false;
              this.shadeTools.errorDialog();
            },
          );
        } else {
          this.submittingPostalCode = false;
          this.editedPostalCode = false;
        }
      },
      () => {
        this.submittingPostalCode = false;
        this.shadeTools.errorDialog();
      },
    );
  }


  submitUpload() {
    if (!this.editedPicture || this.submittingPicture) {
      return;
    }

    this.submittingPicture = true;

    const originalImageUuid = this.app.Image;
    const upload$ = this.fileUploadService.create(this.selectedFile, this.app.ID, ObjectType.APP);

    upload$.subscribe(
      (upload) => {
        this.fileInputEl.nativeElement.value = '';

        this.appsService.patchAppImage$(this.app.ID, upload.UUID).subscribe(
          () => {
            if (this.patchChildApps) {
              this.appsService.patchChildAppImages$(this.app.ID, upload.UUID).subscribe(
                () => {
                  this.deleteUpload(originalImageUuid);
                },
                () => {
                  this.submittingPicture = false;
                  this.shadeTools.errorDialog();
                },
              );
            } else {
              this.deleteUpload(originalImageUuid);
            }
          },
          () => {
            this.submittingPicture = false;
            this.shadeTools.errorDialog();
          },
        );
      },
      () => {
        this.submittingPicture = false;
        this.shadeTools.errorDialog();
      },
    );
  }


  private deleteUpload(uuid: string) {
    if (!uuid) {
      this.submittingPicture = false;
      this.editedPicture = false;

      return;
    }

    this.fileUploadService.delete(uuid).subscribe(
      () => {
        this.submittingPicture = false;
        this.editedPicture = false;
      },
      () => {
        this.submittingPicture = false;
        this.shadeTools.errorDialog();
      },
    );
  }


  deleteApp(): void {
    this.shadeTools.startDialog(
      'Delete App',
      `Are you sure you want to delete the app named ${this.app.Name}? Deleting this app will remove access for all its users and permanently erase its data, but any tiles within it will continue to exist and can be added to other apps.\n\nTo confirm, type DELETE in the box below and confirm.`,
      [
        {
          text: 'Confirm',
          callback: (input: string) => {
            if (input !== 'DELETE') {
              this.shadeTools.errorDialog(
                `The word DELETE must be typed exactly for deletion to occur. Please try again.`,
              );
              return;
            }

            this.appsService.deleteApp$(this.app.ID).subscribe({
              next: () => {
                this.shadeTools.closeAll();
                // Navigate to apps home after successful deletion
                this.router.navigate(['/apps/home']);
              },
              error: () => {
                this.shadeTools.errorDialog();
              }
            });
          },
          color: DialogBoxButtonColor.Red,
        },
        {
          text: 'Cancel',
          callback: () => {
            this.shadeTools.closeAll();
          },
          color: DialogBoxButtonColor.Alt,
        },
      ],
      true,
      false,
      false,
      false,
    );
  }
}
